
import { supabase } from "@/integrations/supabase/client";
import { FamilyInviteResult } from "@/types/database";
import { handleServiceError } from "../utils/errorHandling";
import { familyMembershipService } from "./familyMembershipService";

export const familyInviteService = {
  /**
   * Gets the invite code for a user
   */
  getFamilyInviteCode: async (userId: string): Promise<string | null> => {
    try {
      // Check if user is admin of a family
      const { isAdmin } = await familyMembershipService.canUserLeaveFamilyAsAdmin(userId);
      
      if (!isAdmin) {
        // Non-admin users don't have an invite code
        return null;
      }
      
      // For admin users, get their profile and registration_family_id
      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select("registration_family_id, invite_code")
        .eq("id", userId)
        .single();
        
      if (profileError) throw profileError;
      
      return profileData?.invite_code || null;
    } catch (error) {
      handleServiceError(error, "Failed to get invite code");
      return null;
    }
  },

  /**
   * Generates a new invite code for a user using the existing generate_new_invite_code function
   */
  generateNewInviteCode: async (userId: string): Promise<string | null> => {
    try {
      // Call the database function to generate a new invite code
      const { data, error } = await supabase
        .rpc("generate_new_invite_code", { p_user_id: userId });
        
      if (error) throw error;
      
      return data || null;
    } catch (error) {
      handleServiceError(error, "Failed to generate new invite code");
      return null;
    }
  },

  /**
   * Join a family using an invite code
   */
  joinFamilyByInviteCode: async (userId: string, inviteCode: string): Promise<FamilyInviteResult> => {
    try {
      if (!inviteCode.trim()) {
        return {
          success: false,
          message: "Invite code cannot be empty"
        };
      }
      
      // Check if user is admin of a family with other members
      const { isAdmin, canLeave, message } = await familyMembershipService.canUserLeaveFamilyAsAdmin(userId);
      
      if (isAdmin && !canLeave) {
        return {
          success: false,
          message: "You cannot leave your family because you are the admin and there are other members. Please remove all other members first."
        };
      }
      
      // Call the join_family_by_invite database function which looks for invite codes in profiles
      const { data: familyId, error } = await supabase
        .rpc('join_family_by_invite', {
          p_user_id: userId,
          p_invite_code: inviteCode.trim()
        });
        
      if (error) {
        console.error('Error joining family:', error);
        return {
          success: false,
          message: error.message || "Failed to join family. Invalid invite code or server error."
        };
      }

      return { 
        success: true, 
        message: "Successfully joined family!", 
        familyId: familyId
      };
    } catch (error: any) {
      handleServiceError(error, "Failed to join family by invite code");
      
      return { 
        success: false, 
        message: error.message || "Failed to join family. Invalid invite code or server error."
      };
    }
  }
};
